import { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  // eslint-disable-next-line no-restricted-imports
  Button,
  // eslint-disable-next-line no-restricted-imports
  Icon as ChakraIcon,
  ButtonGroup,
  SimpleGrid,
  Flex,
  List,
  VStack,
  HStack,
  ListItem,
  Tag,
} from '@chakra-ui/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { useContactModal } from 'src/hooks/useContactModal';
import { ContactModal } from 'src/components/molecules/ContactModal';
import { AvailableIcon } from '../../../../../components/atoms/Icon';
import { useNavigate } from 'react-router-dom';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Header } from '../../atoms/Header';
import { Icon } from '../../../../../components/atoms/Icon';
import { Currency, currencySymbol, planPrices } from './prices';

// Every plan card can be selected. The selected card gets the emphasis styles on top.
const planCardProps = {
  cursor: 'pointer',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
  sx: {
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
};

const selectedPlanCardProps = {
  border: '2px solid',
  borderColor: 'icon.green',
  boxShadow: 'lg',
};

const plans = [
  {
    name: 'MMM Starter',
    description: () => (
      <Text>
        For growing brands ready to move beyond platform-reported attribution
      </Text>
    ),
    pricingDescription:
      'For growing brands ready to move beyond platform-reported attribution',
    minPeriod: '3 months',
    upgradeNote: 'Start here, then upgrade to a full plan',
    features: [
      'MMM foundations dashboard',
      'Modeling based on your own digital marketing data',
      'Auto-mapped channels with opinionated defaults',
      'AI-generated insight summaries',
      'Data quality & model readiness checks',
    ],
    extraInfo: [
      {
        title: 'Designed for',
        text: () => (
          <Text w="100%">
            Digital-first advertisers validating measurement before commmiting
            to budgets
          </Text>
        ),
        pricingText:
          'Digital-first advertisers validating measurement before commmiting to budgets',
        icon: 'circle-checkbox',
      },
      {
        title: 'Best suited for',
        text: () => <Text w="100%">Digital only advertisers, SMBs</Text>,
        pricingText: 'Digital only advertisers, SMBs',
        icon: 'graph',
      },
      {
        title: 'Media scope',
        text: () => <Text w="100%">Digital only</Text>,
        pricingText: 'Digital only',
        icon: 'media',
      },
      {
        title: 'Model refresh',
        text: () => <Text w="100%">Monthly</Text>,
        pricingText: 'Monthly',
        icon: 'refresh',
      },
    ],
  },
  {
    name: 'Digital Growth',
    description: () => (
      <Text>
        For performance marketers in digital-only environments
        <br />
        <br />
      </Text>
    ),
    pricingDescription:
      'For performance marketers in digital-only environments',
    minPeriod: '3 months',
    features: [
      'AI-powered dashboard for digital ROI',
      'Modeling based on your own digital marketing data',
      'Auto-generated insights & PDF reports',
      'Scenario planning & budget optimization tools',
      'Built for the Global digital media environment',
    ],
    extraInfo: [
      {
        title: 'Designed for',
        text: () => (
          <Text w="100%">
            Digital-first advertisers with clear performance goals
            <br />
          </Text>
        ),
        pricingText: 'Digital-first advertisers with clear performance goals',
        icon: 'circle-checkbox',
      },
      {
        title: 'Best suited for',
        text: () => (
          <Text>Ecommerce, mobile apps, digital subscription services</Text>
        ),
        pricingText: 'Ecommerce, mobile apps, digital subscription services',
        icon: 'graph',
      },
      {
        title: 'Media scope',
        text: () => <Text>Digital only</Text>,
        pricingText: 'Digital only',
        icon: 'media',
      },
      {
        title: 'Model refresh',
        text: () => <Text>Daily or weekly</Text>,
        pricingText: 'Daily or weekly',
        icon: 'refresh',
      },
    ],
  },
  {
    name: 'Omnichannel Growth',
    highlighted: true,
    description: () => (
      <Text>
        For brands mixing digital and offline media with seasonal sales cycles
      </Text>
    ),
    pricingDescription:
      'For brands mixing digital and offline media with seasonal sales cycles',
    minPeriod: '6 months',
    features: [
      'Full omnichannel ROI dashboard',
      'AI-powered scenario modeling',
      'Digital + offline media contribution',
      'Auto-generated insights & reporting',
      'Seasonal performance evaluation',
    ],
    extraInfo: [
      {
        title: 'Designed for',
        text: () => (
          <Text>
            Hybrid marketers working across media
            <br />
            <br />
          </Text>
        ),
        pricingText: 'Hybrid marketers working across media',
        icon: 'circle-checkbox',
      },
      {
        title: 'Best suited for',
        text: () => (
          <Text>Retail chains, auto dealers, local/national brands</Text>
        ),
        pricingText: 'Retail chains, auto dealers, local/national brands',
        icon: 'graph',
      },
      {
        title: 'Media scope',
        text: () => <Text>Digital & Offline</Text>,
        pricingText: 'Digital & Offline',
        icon: 'media',
      },
      {
        title: 'Model refresh',
        text: () => <Text>Twice/year</Text>,
        pricingText: 'Twice/year',
        icon: 'refresh',
      },
    ],
  },
  {
    minPeriod: '6 months',
    name: 'Enterprise Scale',
    description: () => (
      <Text>
        For mature advertisers with €50k+ daily sales & always-on marketing
      </Text>
    ),
    pricingDescription:
      'For mature advertisers with €50k+ daily sales & always-on marketing',
    features: [
      'Full-stack MMM with frequent refresh cycles',
      'AI-enhanced dashboards + smart recommendations',
      'Detailed flighting & pacing dashboards by campaign type',
      'Strategic scenario builder with ROI forecasting',
      'Multi-brand & multi-market model scaling',
    ],
    extraInfo: [
      {
        title: 'Designed for',
        text: () => <Text>Complex media environments and large teams</Text>,
        pricingText: 'Complex media environments and large teams',
        icon: 'circle-checkbox',
      },
      {
        title: 'Best suited for',
        text: () => (
          <Text>National brands, agencies, multi-market advertisers</Text>
        ),
        pricingText: 'National brands, agencies, multi-market advertisers',
        icon: 'graph',
      },
      {
        title: 'Media scope',
        text: () => <Text>Digital & Offline</Text>,
        pricingText: 'Digital & Offline',
        icon: 'media',
      },
      {
        title: 'Model refresh',
        text: () => <Text>Monthly or Quarterly</Text>,
        pricingText: 'Monthly or Quarterly',
        icon: 'refresh',
      },
    ],
  },
];

export const PricingSection = ({
  showAllFeatures = true,
  showTitle = true,
  showAllPlans = true,
}) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
    'monthly',
  );
  const [currency, setCurrency] = useState<Currency>('eur');
  const navigate = useNavigate();

  const { onOpen, modalProps } = useContactModal({
    defaultInterest: 'pricing',
    title: 'Start Modeling',
    submitButtonText: 'Start Modeling',
  });

  const [selectedPlanName, setSelectedPlanName] = useState(
    () => plans.find((plan) => plan.highlighted)?.name,
  );

  const plansToShow = useMemo(
    () =>
      showAllPlans
        ? plans
        : plans.filter((plan) => plan.name !== 'MMM Starter'),
    [showAllPlans],
  );

  return (
    <>
      <Box
        py={{ base: 16, md: 24 }}
        px={{ base: '2%', lg: 0 }}
        mx="auto"
        color="text.darkBlue.900">
        <Container
          maxW={{ base: 'container.xl', lg: '8xl' }}
          textAlign="center">
          {/* Section Title */}
          {showTitle && <Header>SIMPLE, SCALABLE PRICING</Header>}

          {/* Toggle */}
          <ButtonGroup
            isAttached
            mb={8}
            boxShadow="md"
            borderRadius="full"
            overflow="hidden">
            <Button
              onClick={() => setBillingCycle('monthly')}
              bg={billingCycle === 'monthly' ? 'icon.green' : 'gray.100'}
              color={billingCycle === 'monthly' ? 'white' : 'gray.800'}
              _hover={{
                bg: billingCycle === 'monthly' ? 'icon.green' : 'gray.200',
              }}
              borderRadius="full">
              Monthly
            </Button>
            <Button
              onClick={() => setBillingCycle('yearly')}
              bg={billingCycle === 'yearly' ? 'icon.green' : 'gray.100'}
              color={billingCycle === 'yearly' ? 'white' : 'gray.800'}
              _hover={{
                bg: billingCycle === 'yearly' ? 'icon.green' : 'gray.200',
              }}
              borderRadius="full">
              Annually
            </Button>
          </ButtonGroup>

          <Flex justify="center" mb={8}>
            <ButtonGroup
              isAttached
              boxShadow="md"
              borderRadius="full"
              overflow="hidden">
              <Button
                onClick={() => setCurrency('eur')}
                bg={currency === 'eur' ? 'icon.green' : 'gray.100'}
                color={currency === 'eur' ? 'white' : 'gray.800'}
                _hover={{
                  bg: currency === 'eur' ? 'icon.green' : 'gray.200',
                }}
                borderRadius="full">
                EUR €
              </Button>
              <Button
                onClick={() => setCurrency('usd')}
                bg={currency === 'usd' ? 'icon.green' : 'gray.100'}
                color={currency === 'usd' ? 'white' : 'gray.800'}
                _hover={{
                  bg: currency === 'usd' ? 'icon.green' : 'gray.200',
                }}
                borderRadius="full">
                USD $
              </Button>
            </ButtonGroup>
          </Flex>

          {/* Plan Cards */}
          <Box
            display={
              showAllPlans
                ? { base: 'block', xl: 'none' }
                : { base: 'block', lg: 'none' }
            }>
            {/* Mobile and Tablet Swiper */}
            <Swiper
              modules={[Pagination]}
              spaceBetween={20}
              slidesPerView={1}
              centeredSlides={true}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1.2,
                  spaceBetween: 20,
                  centeredSlides: true,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                  centeredSlides: false,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 32,
                  centeredSlides: false,
                },
              }}
              style={{
                paddingBottom: '50px',
              }}>
              {plansToShow.map((plan) => {
                const prices = planPrices[plan.name][currency];
                const isSelected = plan.name === selectedPlanName;
                const price =
                  billingCycle === 'monthly' ? prices.monthly : prices.yearly;
                const symbol = currencySymbol[currency];
                const period =
                  billingCycle === 'monthly' ? 'Month' : 'Billed Annually';

                return (
                  <SwiperSlide key={plan.name}>
                    <Box
                      bg="background.lightGrey"
                      borderRadius={16}
                      p={{ base: 5, md: 9 }}
                      textAlign="left"
                      {...planCardProps}
                      {...(isSelected ? selectedPlanCardProps : {})}
                      onClick={() => setSelectedPlanName(plan.name)}
                      onFocus={() => setSelectedPlanName(plan.name)}>
                      <Heading
                        fontFamily="marketingPage"
                        as="h3"
                        size="md"
                        mb={2}>
                        {plan.name.toUpperCase()}
                      </Heading>
                      <Text
                        fontFamily="marketingPage"
                        fontSize="sm"
                        color="text.darkBlue.800"
                        mb={4}>
                        {showAllPlans
                          ? plan.pricingDescription
                          : plan.description()}
                      </Text>
                      {billingCycle === 'yearly' && (
                        <Text
                          fontFamily="marketingPage"
                          textDecoration={
                            billingCycle === 'yearly' ? 'line-through' : 'none'
                          }
                          fontSize={billingCycle === 'yearly' ? 'lg' : 'xl'}
                          fontWeight={
                            billingCycle === 'yearly' ? 'normal' : 'bold'
                          }>
                          {symbol}
                          {price}
                        </Text>
                      )}

                      <Flex align="baseline" mb={4}>
                        {billingCycle !== 'yearly' && (
                          <Text
                            fontFamily="marketingPage"
                            textDecoration="none"
                            fontSize="xl"
                            fontWeight="bold"
                            color="text.darkBlue.900">
                            {symbol}
                            {price}
                          </Text>
                        )}
                        {billingCycle === 'yearly' && (
                          <Text
                            fontSize="xl"
                            fontWeight="bold"
                            fontFamily="marketingPage"
                            color="green.500">
                            {symbol}
                            {(
                              parseInt(price.replace(/,/g, ''), 10) * 0.9
                            ).toLocaleString()}
                          </Text>
                        )}
                        <Text
                          fontFamily="marketingPage"
                          fontWeight="medium"
                          ml={1}
                          color="text.darkBlue.900">
                          /{period}
                        </Text>
                        {billingCycle === 'yearly' && (
                          <Tag
                            size="sm"
                            ml={3}
                            variant="subtle"
                            color="white"
                            background="icon.green"
                            fontFamily="marketingPage">
                            first year -10%
                          </Tag>
                        )}

                        {billingCycle === 'monthly' && (
                          <Tag
                            size="sm"
                            ml={3}
                            variant="subtle"
                            color="white"
                            background="icon.green"
                            fontFamily="marketingPage">
                            min period {plan.minPeriod}
                          </Tag>
                        )}
                      </Flex>

                      <Button
                        as="a"
                        href="mailto:support@merkatus.io?subject=Interested%20in%20pricing&body=Hi,%20I%20would%20like%20to%20see%20all%20features"
                        w="full"
                        bg={isSelected ? 'icon.green' : 'background.lightBlue'}
                        color="white"
                        fontFamily="marketingPage"
                        borderRadius={8}
                        mb={plan.upgradeNote ? 2 : 6}>
                        Start Modeling
                      </Button>

                      {plan.upgradeNote && (
                        <Text
                          fontSize="xs"
                          fontFamily="marketingPage"
                          color="text.darkBlue.800"
                          textAlign="center"
                          mb={4}>
                          {plan.upgradeNote}
                        </Text>
                      )}

                      <Text
                        fontFamily="marketingPage"
                        color="text.darkBlue.900"
                        fontWeight="bold"
                        mb={2}>
                        Plan includes
                      </Text>
                      <List spacing={2} color="text.darkBlue.800">
                        {plan.features.map((feature, idx) => (
                          <Flex key={idx}>
                            <ListItem>
                              <ChakraIcon color="icon.green" mr={2}>
                                <Icon iconName="check" />
                              </ChakraIcon>
                            </ListItem>
                            <Text lineHeight="25px">{feature}</Text>
                          </Flex>
                        ))}
                      </List>
                    </Box>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </Box>

          {/* Desktop Grid */}
          <SimpleGrid
            columns={{ base: 1, md: showAllPlans ? 4 : 3 }}
            spacing={showAllPlans ? 4 : 8}
            display={
              showAllPlans
                ? { base: 'none', xl: 'grid' }
                : { base: 'none', lg: 'grid' }
            }>
            {plansToShow.map((plan) => {
              const prices = planPrices[plan.name][currency];
              const isSelected = plan.name === selectedPlanName;
              const price =
                billingCycle === 'monthly' ? prices.monthly : prices.yearly;
              const symbol = currencySymbol[currency];
              const period =
                billingCycle === 'monthly' ? 'Month' : 'Billed Annually';

              return (
                <Box
                  key={plan.name}
                  bg="background.lightGrey"
                  borderRadius={16}
                  position="relative"
                  p={6}
                  textAlign="left"
                  {...planCardProps}
                  {...(isSelected ? selectedPlanCardProps : {})}
                  onClick={() => setSelectedPlanName(plan.name)}
                  onFocus={() => setSelectedPlanName(plan.name)}>
                  <Flex flexDirection="column" h="100%">
                    <Heading
                      fontFamily="marketingPage"
                      as="h3"
                      size="md"
                      mb={2}>
                      {plan.name.toUpperCase()}
                    </Heading>
                    <Text
                      fontFamily="marketingPage"
                      fontSize="sm"
                      color="text.darkBlue.800"
                      mb={4}>
                      {showAllPlans
                        ? plan.pricingDescription
                        : plan.description()}
                    </Text>
                    {billingCycle === 'yearly' && (
                      <Text
                        fontFamily="marketingPage"
                        textDecoration={
                          billingCycle === 'yearly' ? 'line-through' : 'none'
                        }
                        fontSize={billingCycle === 'yearly' ? 'md' : 'xl'}
                        fontWeight={
                          billingCycle === 'yearly' ? 'normal' : 'bold'
                        }>
                        {symbol}
                        {price}
                      </Text>
                    )}

                    <Flex align="baseline" mb={4}>
                      {billingCycle !== 'yearly' && (
                        <Text
                          fontFamily="marketingPage"
                          textDecoration="none"
                          fontSize="xl"
                          fontWeight="bold"
                          color="text.darkBlue.900">
                          {symbol}
                          {price}
                        </Text>
                      )}
                      {billingCycle === 'yearly' && (
                        <Text
                          fontSize="md"
                          fontWeight="bold"
                          fontFamily="marketingPage"
                          color="green.500">
                          {symbol}
                          {(
                            parseInt(price.replace(/,/g, ''), 10) * 0.9
                          ).toLocaleString()}
                        </Text>
                      )}
                      <Text
                        fontFamily="marketingPage"
                        fontWeight="medium"
                        ml={1}
                        color="text.darkBlue.900">
                        /{period}
                      </Text>

                      {billingCycle === 'yearly' && (
                        <Tag
                          size="sm"
                          ml={3}
                          variant="subtle"
                          color="white"
                          background="icon.green"
                          fontFamily="marketingPage">
                          first year -10%
                        </Tag>
                      )}

                      {billingCycle === 'monthly' && (
                        <Tag
                          size="sm"
                          ml={3}
                          variant="subtle"
                          color="white"
                          background="icon.green"
                          fontFamily="marketingPage">
                          {plan.minPeriod === 'No minimum commitment'
                            ? 'No minimum commitment'
                            : `min period ${plan.minPeriod}`}
                        </Tag>
                      )}
                    </Flex>

                    <Button
                      onClick={onOpen}
                      w="full"
                      bg={isSelected ? 'icon.green' : 'background.lightBlue'}
                      color="white"
                      _hover={{
                        bg: 'icon.green',
                      }}
                      fontFamily="marketingPage"
                      borderRadius={8}
                      mb={plan.upgradeNote ? 2 : 6}>
                      Start Modeling
                    </Button>

                    {plan.upgradeNote && (
                      <Text
                        fontSize="xs"
                        fontFamily="marketingPage"
                        color="text.darkBlue.800"
                        textAlign="center"
                        mb={4}>
                        {plan.upgradeNote}
                      </Text>
                    )}

                    <VStack align="stretch" mb={6} spacing={3}>
                      {plan.extraInfo?.map((info, idx) => (
                        <HStack key={idx} align="flex-start" spacing={2}>
                          <Flex align="center" justify="center">
                            <ChakraIcon width={5} height={5} color="icon.green">
                              <Icon iconName={info.icon as AvailableIcon} />
                            </ChakraIcon>
                          </Flex>
                          <Flex w="100%">
                            <Text as="span" fontWeight="bold" w="100%">
                              {info.title}:
                            </Text>{' '}
                            <Text w="100%">
                              {showAllPlans ? info.pricingText : info.text()}
                            </Text>
                          </Flex>
                        </HStack>
                      ))}
                    </VStack>

                    <Text
                      fontFamily="marketingPage"
                      color="text.darkBlue.900"
                      fontWeight="bold"
                      mb={2}>
                      Plan includes
                    </Text>
                    <List spacing={2} color="text.darkBlue.800">
                      {plan.features.map((feature, idx) => (
                        <Flex key={idx}>
                          <ListItem>
                            <ChakraIcon color="icon.green" mr={2}>
                              <Icon iconName="check" />
                            </ChakraIcon>
                          </ListItem>
                          <Text lineHeight="25px">{feature}</Text>
                        </Flex>
                      ))}
                    </List>
                    {showAllFeatures && (
                      <Box textAlign="center" mt="auto">
                        <Text
                          as="button"
                          onClick={() => navigate('/pricing')}
                          color="text.darkBlue.800"
                          fontFamily="marketingPage"
                          fontSize="md"
                          fontWeight="normal"
                          textDecoration="underline"
                          textUnderlineOffset="4px"
                          cursor="pointer">
                          See all features
                        </Text>
                      </Box>
                    )}
                  </Flex>
                </Box>
              );
            })}
          </SimpleGrid>
        </Container>
      </Box>
      <ContactModal {...modalProps} />
    </>
  );
};
